import { H6 } from '@blueprintjs/core';
import styles from './ApplicationDataLoanInformation.module.css';
import { ApplicationDataNumberField } from './Fields/ApplicationDataNumberField';
import { ApplicationDataCurrencyField } from './Fields/ApplicationDataCurrencyField';
import { ApplicationDataSelectField } from './Fields/ApplicationDataSelectField';
import { DebtType, Product, Purpose } from '@axo/shared/data-access/types';
import { useFormContext } from 'react-hook-form';
import { ApplicationDataBaseField } from './Fields/ApplicationDataBaseField';
import { useLoanApplication } from '@axo/shared/data-access/hooks';
import { currencyFormatter } from '@axo/shared/util/formatters';
import { ApplicationDataFormValues } from './ApplicationDataForm/ApplicationDataFormSchema';
import { MarketCountryCode } from '@axo/shared/types';
import { Broker } from '@axo/shared/data-access/types/broker';
import { ApplicationConditionalWrapper } from '../ConditionalWrappers/ApplicationConditionalWrapper';
import { useLoanPurposeOptions } from './ApplicationDataApplicantsTabs/Sections/options/useLoanPurposeOptions';
import { RefinanceAmountField } from './CalculatedFields/RefinanceAmountField';
import { ApplicationDataTextField } from './Fields/ApplicationDataTextField';

interface ApplicationDataLoanInformationProps {
  applicationID: string;
}

export const ApplicationDataLoanInformation: React.FC<
  ApplicationDataLoanInformationProps
> = ({ applicationID }) => {
  const { data: applicationData } = useLoanApplication(applicationID);

  const { watch, setValue } = useFormContext<ApplicationDataFormValues>();
  const debts = watch('Debts');
  const purpose = watch('Purpose');

  const loanPurposeOptions = useLoanPurposeOptions();

  const monthlyRefinanceCost =
    debts?.reduce((current, debt) => {
      if (debt.Type === DebtType.Unspecified)
        return current + (debt.MonthlyDebtPayment ?? 0);
      return current;
    }, 0) ?? 0;

  if (!applicationData) return null;

  return (
    <div className={styles.rootContainer}>
      <H6 className={styles.header}>Personal</H6>
      <ApplicationDataCurrencyField
        applicationID={applicationID}
        name="AppliedAmount"
        label="Loan amount"
        horizontal
      />
      <ApplicationConditionalWrapper
        whitelist={[
          {
            productType: [Product.UnsecuredLoan],
          },
        ]}
      >
        <ApplicationDataNumberField
          applicationID={applicationID}
          name="LoanDuration"
          label="Loan period"
          formatter={(value) => (value ? `${String(value)}yr` : '-')}
          horizontal
        />
      </ApplicationConditionalWrapper>
      <ApplicationConditionalWrapper
        whitelist={[{ productType: [Product.UnsecuredLoan] }]}
      >
        <RefinanceAmountField applicationID={applicationID} />
      </ApplicationConditionalWrapper>
      <ApplicationConditionalWrapper
        whitelist={[
          {
            marketCountry: [MarketCountryCode.Sweden],
            productType: [Product.UnsecuredLoan],
            broker: [Broker.Zmarta],
          },
        ]}
      >
        <ApplicationDataBaseField
          applicationID={applicationID}
          label="Ref. monthly cost"
          valueElement={
            monthlyRefinanceCost
              ? currencyFormatter(
                  monthlyRefinanceCost,
                  applicationData.Currency ?? undefined,
                  0,
                )
              : '-'
          }
          horizontal
        />
      </ApplicationConditionalWrapper>
      <ApplicationConditionalWrapper
        whitelist={[
          {
            productType: Product.UnsecuredLoan,
          },
        ]}
      >
        <ApplicationDataSelectField
          applicationID={applicationID}
          name="Purpose"
          label="Loan purpose"
          options={loanPurposeOptions}
          horizontal
          onChangeSideEffect={(value) => {
            if (
              [
                Purpose.Other,
                Purpose.Medical,
                Purpose.Holiday,
                Purpose.Renovation,
                Purpose.Vehicle,
              ].includes(value as Purpose)
            ) {
              debts.forEach((debt, index) => {
                if (debt.RefinanceAmount) {
                  setValue(`Debts.${index}.RefinanceAmount`, 0);
                }
              });
            }

            if (value === Purpose.Other) {
              setValue('OtherPurpose', null);
            }
          }}
        />
      </ApplicationConditionalWrapper>
      <ApplicationConditionalWrapper
        whitelist={[
          {
            productType: [Product.UnsecuredLoan],
            marketCountry: [MarketCountryCode.Denmark],
          },
        ]}
      >
        {purpose === Purpose.Other && (
          <ApplicationDataTextField
            applicationID={applicationID}
            name="OtherPurpose"
            placeholder="Other purpose"
            horizontal
          />
        )}
      </ApplicationConditionalWrapper>
    </div>
  );
};
